import axiosInstance from './axiosConfig';

export type AdReportFrequency = 'daily' | 'weekly' | 'monthly';

export interface AdReportSetting {
  /** 商品単位の設定なら商品 id。組織全体の設定は null。 */
  product_id?: number | null;
  enabled: boolean;
  frequency: AdReportFrequency;
  monthly_budget: number | null;
  target_cpa: number | null;
  /** 秘匿値そのものは返らない。設定済みかどうかだけ。 */
  has_discord_webhook: boolean;
  has_slack_webhook: boolean;
  has_chatwork_token: boolean;
  chatwork_room_id: string | null;
  email_recipients: string[];
  last_sent_at: string | null;
}

/**
 * 更新は「送るキーだけ」が書き換わる。webhook を伏字表示したまま頻度だけ変えたい
 * ケースがあるので、未変更の秘匿値はキーごと送らないこと。
 */
export interface AdReportSettingInput {
  enabled?: boolean;
  frequency?: AdReportFrequency;
  monthly_budget?: number | null;
  target_cpa?: number | null;
  discord_webhook_url?: string | null;
  slack_webhook_url?: string | null;
  chatwork_room_id?: string | null;
  chatwork_api_token?: string | null;
  email_recipients?: string[];
  /** 商品単位の設定を更新するとき。組織全体ならキーごと送らない。 */
  product_id?: number;
}

/** 通知の対象。先頭は組織全体、続いて広告を紐付けた商品。 */
export interface AdReportTarget {
  product_id: number | null;
  name: string;
  enabled: boolean;
  has_destination: boolean;
  /** 広告の紐付けが外れた商品の設定は false。配信を止められるよう一覧には残る。 */
  linked: boolean;
}

export interface AdReportTestResult {
  results: Record<string, boolean>;
  preview: string;
  message: string;
}

const productBody = (productId: number | null): { product_id?: number } =>
  productId !== null ? { product_id: productId } : {};

export const getAdReportTargets = async (): Promise<AdReportTarget[]> => {
  const response = await axiosInstance.get<{ success: boolean; data: { targets: AdReportTarget[] } }>('/ad-report/targets');
  return response.data.data.targets;
};

export const getAdReportSetting = async (productId: number | null = null): Promise<AdReportSetting> => {
  const response = await axiosInstance.get<{ setting: AdReportSetting }>('/ad-report/settings', {
    params: productBody(productId),
  });
  return response.data.setting;
};

export const updateAdReportSetting = async (
  input: AdReportSettingInput
): Promise<AdReportSetting> => {
  const response = await axiosInstance.put<{ setting: AdReportSetting }>(
    '/ad-report/settings',
    input
  );
  return response.data.setting;
};

export const sendAdReportTest = async (productId: number | null = null): Promise<AdReportTestResult> => {
  const response = await axiosInstance.post<AdReportTestResult>('/ad-report/test', productBody(productId));
  return response.data;
};

const adReportService = {
  getAdReportTargets,
  getAdReportSetting,
  updateAdReportSetting,
  sendAdReportTest,
};

export default adReportService;
